/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import {ISelect} from '../../interface/ISelect';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import deviceFuelAPI from '../../services/modules/JSON/deviceFuel';
import efdbJson from '../../services/modules/JSON/efdbJson';
import refrigerantUsedJSON from '../../services/modules/JSON/refriUsed';
import DceAPI from '../../services/modules/PRODUCTS/DceAPI';
import productInfoAPI from '../../services/modules/PRODUCTS/ProductInfoAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {B2bKey} from '../Forms/Constants/Products/B2BKeyLabel';
import {DceKey, DceLabel} from '../Forms/Constants/Products/DceKeyLabel';
import {
  RawMaterialKey,
  RawMaterialLabel
} from '../Forms/Constants/Products/RawMaterialKeyAndLabel';
import DceForm from '../Forms/DceForm';
import changeUnit from '../Forms/Utils/changeUnit';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import Loader from '../Loader/Loader';

import {FullInfo, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {returnProductFilter} from './Utils/returnProductFilters';
import sumValue from './Utils/sumValue';
import {
  excludeAcceptedIdsForDelete,
  getBulkSelectedIdsInDataSource,
  useBulkCheckerModals
,
  createBulkRowSelection
} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const DceTable: React.FC<{
  selectedYear: string;
}> = ({selectedYear}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    fuelType: string;
    noOfUnits: number;
    deviceType: number;
    quantity: number;
    cost: number;
    driven: number;
    status?: string;
  }

  const {companyId, preUnits} = useCompanyStore.getState();
  const {electricity} = preUnits;
  const [rawMaterialFilter, setRawMaterialFilter] = useState<any>({});
  const [formRawMaterialFilter, setFormRawMaterialFilter] = useState<any>({});
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formSelectedYear, setFormSelectedYear] = useState('');

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const bulkSelectedIds = useMemo(
    () => getBulkSelectedIdsInDataSource(selectedRowKeys, dataSource),
    [selectedRowKeys, dataSource]
  );
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const products = useJSONStore.getState().products;
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });

  const [allJSON, setAllJSON] = useState(false);
  const {deviceFuel, refridgentUsed} = useJSONStore.getState();

  useEffect(() => {
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        DceAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`),
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        )
      ])
        .then(([res1, res2]) => {
          setDataSource(res1 ?? []);
          setRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                }
              ],
              res2,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    if (formSelectedYear) {
      Promise.all([
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${formSelectedYear}`
        )
      ])
        .then(([res1]) => {
          setFormRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                }
              ],
              res1,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setIsFormLoading(false);
        });
    }
  }, [formSelectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      DceAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([deviceFuelAPI(), refrigerantUsedJSON(), efdbJson()])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];
    const consumeEnergy = excludeAcceptedIdsForDelete(ids, dataSource);
    if (consumeEnergy.length === 0) {
      return;
    }
    DceAPI(APITypeEnum.DELETE_ALL, {
      consumeEnergy
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const bulkChecker = useBulkCheckerModals({
    selectedIds: bulkSelectedIds,
    refresh,
    selectionContextKey: selectedYear,
    onSelectionContextChange: () => {
      setSelectedRowKeys([]);
    },
    getBulkDeleteEligibleCount: (ids) =>
      excludeAcceptedIdsForDelete(ids, dataSource).length,
    onSuccess: () => {
      setSelectedRowKeys([]);
      setRefresh(true);
    },
    onBulkStatusSubmit: async ({ids, status, comment}) =>
      await DceAPI(APITypeEnum.STATUS_ALL, {
        consumeEnergy: ids,
        status,
        [checkerStatusKey.COMMENT]: comment
      }),
    onBulkDelete: async (ids) => {
      const consumeEnergy = excludeAcceptedIdsForDelete(ids, dataSource);
      if (consumeEnergy.length === 0) {
        return false;
      }

      return await DceAPI(APITypeEnum.DELETE_ALL, {
        consumeEnergy
      });
    }
  });

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const {productFilter} = rawMaterialFilter;

  const dceKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: DceKey.PRODUCT_ID,
        label: DceLabel.PRODUCT_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter.productFilter
          : rawMaterialFilter.productFilter
      },
      {
        key: DceKey.LIFE_TIME,
        label: DceLabel.LIFE_TIME,
        type: objTypes.number
      },
      {
        key: TF,
        label: DceLabel.UNIT_SOLD_TF,
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      {
        key: DceKey.FUEL_TYPE,
        label: DceLabel.FUEL_TYPE,
        type: objTypes.json,
        format: deviceFuel
      },
      {
        key: DceKey.FUEL_USED,
        label: DceLabel.FUEL_USED,
        type: objTypes.relatedUnit,
        relatedKey: DceKey.FUEL_TYPE,
        format: deviceFuel
      },
      {
        key: DceKey.REFRIGERATOR_TYPE,
        label: DceLabel.REFRIGERATOR_TYPE,
        type: objTypes.json,
        format: refridgentUsed
      },
      {
        key: DceKey.REFRIGERATOR_USED,
        label: DceLabel.REFRIGERATOR_USED,
        type: objTypes.relatedUnit,
        relatedKey: DceKey.REFRIGERATOR_TYPE,
        format: refridgentUsed
      },
      {
        key: DceKey.ELEC_CONSUME,
        label: DceLabel.ELEC_CONSUME,
        type: objTypes.number,
        unit: 'kWh'
      },
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              key: checkerStatusKey.STATUS,
              label: checkerStatusLabel.STATUS,
              type: objTypes.status
            },
            {
              key: checkerStatusKey.COMMENT,
              label: checkerStatusLabel.COMMENT,
              type: objTypes.comment
            }
          ]
        : [])
    ];
  }, [formRawMaterialFilter, rawMaterialFilter]);

  const columns: any = [
    {
      title: DceLabel.PRODUCT_ID,
      dataIndex: DceKey.PRODUCT_ID,
      editable: true,
      type: 'select',
      ...SearchFilter(DceLabel.PRODUCT_ID, DceKey.PRODUCT_ID, productFilter),
      render: (value: any) => {
        return (
          <>
            {
              productFilter?.find((item: any) => {
                return item.value === value;
              })?.label
            }
          </>
        );
      }
    },
    {
      title: DceLabel.LIFE_TIME,
      dataIndex: DceKey.LIFE_TIME,
      render: (value: any) => {
        return `${value ?? '0'}`;
      }
    },
    {
      title: DceLabel.UNIT_SOLD_TF,
      dataIndex: TF,
      editable: true,
      type: 'select',
      render: (value: any) => {
        const mainData = value?.find((item: any) => {
          return item.type === DceKey.UNIT_SOLD_TF;
        });

        return (
          <div
            onClick={() => {
              setOpenDisplayModal(true);
              setOpenDisplayModalData(mainData);
              setOpenDisplayModalType({
                title: `${DceLabel.UNIT_SOLD_TF} (${mainData?.unit})`,
                dataKey: DceKey.UNIT_SOLD_TF
              });
            }}
            style={{
              textDecorationLine: 'underline',
              pointerEvents: 'auto',
              cursor: 'pointer'
            }}
          >
            <div>{`${String(
              formatIndian(sumValue(mainData?.data))
            )} ${mainData?.unit}`}</div>
          </div>
        );
      }
    },
    {
      title: DceLabel.FUEL_TYPE,
      dataIndex: DceKey.FUEL_TYPE,
      ...SearchFilter(DceLabel.FUEL_TYPE, DceKey.FUEL_TYPE, deviceFuel),
      render: (value: any) => {
        return (
          <>
            {deviceFuel?.find((item: any) => {
              return item.value === value;
            })?.label ?? '-'}
          </>
        );
      }
    },
    {
      title: DceLabel.FUEL_USED,
      dataIndex: DceKey.FUEL_USED,
      render: (value: any, record: any) => {
        return `${formatIndian(value) ?? '0'} ${changeUnit(
          deviceFuel?.find((item: any) => {
            return item.value === record?.[DceKey.FUEL_TYPE];
          })?.state
        )}`;
      }
    },
    {
      title: DceLabel.REFRIGERATOR_TYPE,
      dataIndex: DceKey.REFRIGERATOR_TYPE,
      ...SearchFilter(
        DceLabel.REFRIGERATOR_TYPE,
        DceKey.REFRIGERATOR_TYPE,
        refridgentUsed
      ),
      render: (value: any) => {
        return (
          <>
            {refridgentUsed?.find((item: any) => {
              return item.value === value;
            })?.label ?? '-'}
          </>
        );
      }
    },
    {
      title: DceLabel.REFRIGERATOR_USED,
      dataIndex: DceKey.REFRIGERATOR_USED,
      render: (value: any, record: any) => {
        return `${formatIndian(value) ?? '0'} ${changeUnit(
          refridgentUsed?.find((item: any) => {
            return item.value === record?.[DceKey.REFRIGERATOR_TYPE];
          })?.state
        )}`;
      }
    },
    {
      title: DceLabel.ELEC_CONSUME,
      dataIndex: DceKey.ELEC_CONSUME,
      render: (value: any) => {
        const fieldValue = formatIndian(value);

        return `${fieldValue ?? '0'} ${fieldValue === '-' ? '' : electricity}`;
      }
    },
    ...(hasUserRole(UserRole.Maker_Role) ||
    hasUserRole(UserRole.Both_role) ||
    hasViewRole(UserRole.View_Only)
      ? [
          {
            title: RawMaterialLabel.STATUS_COMMENTS,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment">- {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    // ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
    //   ? [
    //       {
    //         title: checkerStatusLabel.DATA_SOURCE,
    //         dataIndex: checkerStatusKey.DATA_SOURCE,
    //         editable: true,
    //         type: 'select',
    //         render: (value: any) => {
    //           return (
    //             <div>
    //               {value === checkerStatusKey.MANUAL
    //                 ? checkerStatusLabel.MANUAL
    //                 : checkerStatusLabel.BULK_UPLOAD}
    //             </div>
    //           );
    //         }
    //       }
    //     ]
    //   : []),
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: RawMaterialLabel.STATUS,
            dataIndex: 'status',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <Tag
                  onClick={() => {
                    if (shouldBlockReadOnly()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }

                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: FullInfo.title,
            dataIndex: 'fullInfo',
            editable: true,
            type: 'select',
            render: (_: any, record: any) => {
              return (
                <Button
                  onClick={() => {
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  shape="circle"
                  style={{
                    marginRight: 16,
                    cursor: 'pointer',
                    color: '#435B37',
                    background: '#F5FBF3',
                    border: 0
                  }}
                >
                  <ExclamationOutlined />
                </Button>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                />
              ) : null
          }
        ]
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Sold Products Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Sold Product'
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs: null, uploadType: ''}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        tooltip={InformativeText.Product.dce}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
        onSelectAll={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? handleSelectAll
            : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        onChangeStatus={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openChangeStatus
            : undefined
        }
        onBulkDelete={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openBulkDelete
            : undefined
        }
      />
      {bulkChecker.bulkCheckerModals}
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          },
          enabled:
            hasUserRole(UserRole.Maker_Role) ||
            hasUserRole(UserRole.Both_role)
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
          setFormSelectedYear('');
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={
          <DceForm
            rawMaterialFilter={
              formSelectedYear ? formRawMaterialFilter : rawMaterialFilter
            }
            data={isEdit ? editData : undefined}
            setFormSelectedYear={setFormSelectedYear}
            setIsFormLoading={setIsFormLoading}
            isFormLoading={isFormLoading}
          />
        }
        API={DceAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        onlyYear={true}
        stepsTitle={['Sold Product Details', 'Quantity Details']}
        formPreview={dceKeyLabel}
      />
      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          <TimeFrameDataTooltip mainData={openDisplayModalData} />
        }
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        API={DceAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={dceKeyLabel} />
        }
        table={true}
        stepsTitle={['All Data']}
        isChecker={true}
        isAction={false}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={dceKeyLabel} />
        }
        API={DceAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['All Data']}
      />
    </div>
  );
};

export default DceTable;
